import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Smartphone, X, Share, Plus as PlusIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

function isStandalone() {
  return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
}

function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

export function PWAInstallPrompt() {
  const [show, setShow] = useState(false);
  const [deferred, setDeferred] = useState(null);
  const [showIOSGuide, setShowIOSGuide] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    const dismissed = localStorage.getItem('regen_pwa_dismissed');
    if (dismissed === 'forever') return;
    if (dismissed && (Date.now() - parseInt(dismissed)) < 24 * 60 * 60 * 1000) return;

    const onBeforeInstall = (e) => { e.preventDefault(); setDeferred(e); setShow(true); };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);

    // iOS: no beforeinstallprompt — show manual guide after small delay
    if (isIOS()) {
      const t = setTimeout(() => setShow(true), 1500);
      return () => { window.removeEventListener('beforeinstallprompt', onBeforeInstall); clearTimeout(t); };
    }
    return () => window.removeEventListener('beforeinstallprompt', onBeforeInstall);
  }, []);

  const install = async () => {
    if (isIOS()) { setShowIOSGuide(true); return; }
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === 'accepted') localStorage.setItem('regen_pwa_dismissed', 'forever');
    setShow(false);
  };

  const later = () => {
    localStorage.setItem('regen_pwa_dismissed', String(Date.now()));
    setShow(false);
  };

  const never = () => {
    localStorage.setItem('regen_pwa_dismissed', 'forever');
    setShow(false);
  };

  if (isStandalone()) return null;

  return (
    <>
      <AnimatePresence>
        {show && !showIOSGuide && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            className="fixed bottom-24 lg:bottom-6 inset-x-4 max-w-md mx-auto z-[60] regen-card-elevated p-4 shadow-2xl border-[#00C8FF]/30"
            data-testid="pwa-prompt"
          >
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#00C8FF] to-[var(--c-green)] flex items-center justify-center flex-shrink-0">
                <Smartphone className="w-5 h-5 text-[#081726]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold text-sm">Install YourReGen on Your Phone</div>
                <div className="text-[11px] text-muted-foreground mt-0.5">Faster access, notifications and a real app feel.</div>
              </div>
              <button onClick={later} className="w-8 h-8 -mt-1 -mr-1 rounded-full hover:bg-[var(--c-film)] flex items-center justify-center flex-shrink-0">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex gap-2 mt-3">
              <Button onClick={install} data-testid="pwa-install" size="sm" className="flex-1 bg-[#00C8FF] text-[#081726] font-semibold h-9">Install App</Button>
              <Button onClick={later} data-testid="pwa-later" size="sm" variant="outline" className="border-[var(--c-hair)] h-9">Later</Button>
              <Button onClick={never} data-testid="pwa-never" size="sm" variant="ghost" className="text-muted-foreground h-9 px-2">Don't show</Button>
            </div>
          </motion.div>
        )}

        {showIOSGuide && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-end justify-center p-4">
            <motion.div initial={{ y: 100 }} animate={{ y: 0 }} className="regen-card-elevated p-6 w-full max-w-md" data-testid="pwa-ios-guide">
              <div className="flex items-start justify-between">
                <div className="font-display font-black text-xl">Install on iPhone</div>
                <button onClick={() => { setShowIOSGuide(false); setShow(false); later(); }} className="w-8 h-8 rounded-full hover:bg-[var(--c-film)] flex items-center justify-center"><X className="w-4 h-4" /></button>
              </div>
              <div className="mt-4 space-y-3 text-sm">
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-full bg-[#00C8FF]/15 text-[#00C8FF] flex items-center justify-center text-xs font-bold">1</div>
                  <div className="flex items-center gap-2">Tap <Share className="w-4 h-4 text-[#00C8FF]" /> Share button</div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-full bg-[#00C8FF]/15 text-[#00C8FF] flex items-center justify-center text-xs font-bold">2</div>
                  <div className="flex items-center gap-2">Tap <PlusIcon className="w-4 h-4 text-[#00C8FF]" /> Add to Home Screen</div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-full bg-[#00C8FF]/15 text-[#00C8FF] flex items-center justify-center text-xs font-bold">3</div>
                  <div>Tap <span className="text-[#00C8FF] font-semibold">Add</span> to confirm</div>
                </div>
              </div>
              <Button onClick={() => { setShowIOSGuide(false); never(); }} className="mt-5 w-full bg-[#00C8FF] text-[#081726] font-semibold">Got it</Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
